import { BaseApi } from "../BaseApi";
import config from "../../../config";

let API: BaseApi | null = null;

function getApiInstance(): BaseApi {
  if (!API) {
    API = new BaseApi();
  }
  return API;
}

function connectShopify(shop: string, boutiqueId: string) {
  const params = new URLSearchParams({ shop, boutiqueId });
  window.location.href = `${config.API_URL}/api/integrations/shopify/connect?${params.toString()}`;
}

function connectYoucan(storeId: string, boutiqueId: string) {
  const params = new URLSearchParams({ storeId, boutiqueId });
  window.location.href = `${config.API_URL}/api/integrations/youcan/connect?${params.toString()}`;
}

function connectFlexiopage(
  boutiqueId: string,
  storeId: string,
  storeName?: string,
  webhookSecret?: string
) {
  return getApiInstance().post("integrations/flexiopage/connect", {
    boutiqueId,
    storeId,
    storeName,
    webhookSecret,
  });
}

function connectGoogleSheets(returnTo?: string) {
  const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
  window.location.href = `${config.API_URL}/api/integrations/google/connect${params}`;
}

function listGoogleSheets() {
  return getApiInstance().get("integrations/google/sheets");
}

function selectGoogleSheet(sheetId: string, sheetName: string) {
  return getApiInstance().post("integrations/google/select-sheet", {
    sheetId,
    sheetName,
  });
}

function syncGoogleSheetsNow(sellerId?: string) {
  return getApiInstance().post(
    "integrations/google/sync-now",
    sellerId ? { sellerId } : {}
  );
}

/**
 * Disconnects a specific integration when integrationId is provided, or
 * every integration of this platform for the seller when it's omitted
 * (matches the legacy single-store semantics).
 */
function disconnectPlatform(
  platform: string,
  options?: { sellerId?: string; integrationId?: string }
) {
  const baseUrl = `integrations/${platform}/disconnect`;
  const body: Record<string, string> = {};
  if (options?.sellerId) body.sellerId = options.sellerId;
  if (options?.integrationId) body.integrationId = options.integrationId;
  return getApiInstance().post(baseUrl, body);
}

function getIntegrationStatus(sellerId?: string) {
  const params = sellerId ? { sellerId } : {};
  const query = new URLSearchParams(params as Record<string, string>).toString();
  const url = query ? `integrations/status?${query}` : "integrations/status";
  return getApiInstance().get(url);
}

function syncProducts(
  platform: string,
  options?: { sellerId?: string; integrationId?: string }
) {
  const baseUrl = `integrations/${platform}/sync-products`;
  const body: Record<string, string> = {};
  if (options?.sellerId) body.sellerId = options.sellerId;
  if (options?.integrationId) body.integrationId = options.integrationId;
  return getApiInstance().post(baseUrl, body);
}

// ----- Admin / supervisor multi-store management -----

function adminListIntegrations(params?: {
  skip?: number;
  limit?: number;
  keyword?: string;
  platform?: string;
  sellerId?: string;
  status?: "enabled" | "disabled" | "";
}) {
  const qs: Record<string, string> = {};
  if (params?.skip !== undefined) qs.skip = String(params.skip);
  if (params?.limit !== undefined) qs.limit = String(params.limit);
  if (params?.keyword) qs.keyword = params.keyword;
  if (params?.platform) qs.platform = params.platform;
  if (params?.sellerId) qs.sellerId = params.sellerId;
  if (params?.status) qs.status = params.status;
  const query = new URLSearchParams(qs).toString();
  const url = query ? `integrations/admin/list?${query}` : "integrations/admin/list";
  return getApiInstance().get(url);
}

function adminGetIntegration(id: string) {
  return getApiInstance().get(`integrations/admin/${id}`);
}

function adminToggleIntegration(id: string, enabled: boolean) {
  return getApiInstance().put(`integrations/admin/${id}/enabled`, { enabled });
}

function adminForceDisconnect(id: string) {
  return getApiInstance().delete(`integrations/admin/${id}`);
}

// ----- Admin: FlexioPage dispatch health & secret re-sync -----

function adminFlexioDispatchHealth() {
  return getApiInstance().get("integrations/admin/flexiopage/dispatch-health");
}

function adminSetWebhookSecret(id: string, secret: string) {
  return getApiInstance().put(`integrations/admin/${id}/webhook-secret`, {
    secret,
  });
}

function adminTestDispatch(id: string) {
  return getApiInstance().post(`integrations/admin/${id}/test-dispatch`, {});
}

function adminClearWebhookSecret(id: string) {
  return getApiInstance().delete(`integrations/admin/${id}/webhook-secret`);
}

function adminClearAllFlexioSecrets() {
  return getApiInstance().post("integrations/admin/flexiopage/clear-secrets", {});
}

export {
  connectShopify,
  connectYoucan,
  connectFlexiopage,
  connectGoogleSheets,
  listGoogleSheets,
  selectGoogleSheet,
  syncGoogleSheetsNow,
  disconnectPlatform,
  getIntegrationStatus,
  syncProducts,
  adminListIntegrations,
  adminGetIntegration,
  adminToggleIntegration,
  adminForceDisconnect,
  adminFlexioDispatchHealth,
  adminSetWebhookSecret,
  adminTestDispatch,
  adminClearWebhookSecret,
  adminClearAllFlexioSecrets,
};
